// Organization Form Modal - Refactored for creating/editing organizations
import React, { useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import { IOrganization } from '@/types/admin/organizations';
import { ADMIN_COLORS } from '@/constants/admin';
import { useOrganizationForm } from '@/hooks/admin/useOrganizationForm';
import { KeyboardSafeView } from '@/components/common/KeyboardSafeView';
import { SVG_COMPONENTS } from '@/assets/admin';
import { OrganizationCreateForm } from './OrganizationCreateForm';
import { OrganizationEditForm } from './OrganizationEditForm';

interface OrganizationFormModalProps {
  visible: boolean;
  onClose: () => void;
  organization?: IOrganization | null;
  onSuccess?: () => void;
}

export const OrganizationFormModal: React.FC<OrganizationFormModalProps> = ({
  visible,
  onClose,
  organization,
  onSuccess,
}) => {
  const {
    formData,
    errors,
    isEditMode,
    isLoadingConfigs,
    isLoading,
    isUpdating,
    isDeleting,
    isPatchingConfig,
    updateField,
    updatePaymentAccount,
    resetForm,
    createOrganization,
    updateOrganization,
    removeOrganization,
  } = useOrganizationForm({ organization, onSuccess, onClose });

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <KeyboardSafeView isModal>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>
              {isEditMode ? organization?.name : 'Create Organization'}
            </Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <SVG_COMPONENTS.CANCEL_X width={24} height={24} color={ADMIN_COLORS.TEXT.PRIMARY} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          {isEditMode && organization ? (
            <OrganizationEditForm
              organization={organization}
              formData={formData}
              errors={errors}
              isLoadingConfigs={isLoadingConfigs}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              isPatchingConfig={isPatchingConfig}
              onNameChange={(text) => updateField('name', text)}
              onDomainChange={(text) => updateField('domain', text)}
              onEntrataUsersChange={(users) => updateField('entrataUsers', users)}
              onPaymentAccountChange={updatePaymentAccount}
              onUpdate={updateOrganization}
              onDelete={removeOrganization}
            />
          ) : (
            <OrganizationCreateForm
              formData={formData}
              errors={errors}
              isLoading={isLoading}
              onNameChange={(text) => updateField('name', text)}
              onDomainChange={(text) => updateField('domain', text)}
              onEntrataUsersChange={(users) => updateField('entrataUsers', users)}
              onPaymentAccountChange={updatePaymentAccount}
              onSubmit={createOrganization}
              onCancel={handleClose}
            />
          )}
        </View>
      </KeyboardSafeView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  closeButton: {
    padding: 8,
  },
});
